import { createContext, useContext } from 'react';
import { useQuery } from 'convex/react';
import { useConvexAuth } from 'convex/react';
import { useAuthActions } from '@convex-dev/auth/react';
import { api } from '../convex/_generated/api';
import { User } from '../types/user';

interface AuthContextType {
  userData: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const { signIn: convexSignIn, signOut: convexSignOut } = useAuthActions();
  const me = useQuery(api.users.getMe);

  const signIn = async (email: string, password: string) => {
    await convexSignIn('password', { email, password, flow: 'signIn' });
  };

  const signOut = async () => {
    await convexSignOut();
  };

  const userData: User | null = me
    ? {
        uid: me._id,
        email: me.email,
        name: me.name,
        role: me.role,
        url: me.url,
        phone: me.phone,
        immunocalUrl: me.immunocalUrl,
        consultorId: me.consultorId,
      }
    : null;

  const loading = isLoading;

  const value = {
    userData,
    loading,
    isAuthenticated,
    signIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
